import {
  Autocomplete,
  Checkbox,
  Collapse,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  TextField,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import CustomAutocompleteMulti from "../../../../common/components/functional/customAutocomplete/CustomAutocompleteMulti";
import { AutocompleteType } from "../../../../common/types/autoComplete";
import {
  fetchCustomerOrgs,
  selectAllCustomerOrganizations,
} from "../../../customer/customerOrganizationsSlice";
import { CustomerOrganizationsResult } from "../../../customer/types/queries";
import { JOB_AD_CAMPAIGNS } from '../../constants';
import { StyledSelect } from "../../styles";
import { JobAdIntegrationType, SectionProps } from "../../types/common";
import FormGrid from "../FormGrid";
import FormSectionHeader from "../FormSectionHeader";
import ImageUploadRow from "../ImageUploadRow";
import PreviewField from "../PreviewField";

const EezySection = ({
  isEditing,
  integrationsToPublish,
  selectedJobAd,
}: SectionProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(true);

  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const integrationType = JobAdIntegrationType.Eezy;
  const integrationIsSelectedToBePublished =
    integrationsToPublish?.includes(integrationType);

  const customerOrganizations = useAppSelector((state) =>
    selectAllCustomerOrganizations(state),
  );

  useEffect(() => {
    dispatch(fetchCustomerOrgs({}));
  }, [dispatch]);

  const {
    control,
    watch,
    formState: { errors },
    setValue,
  } = useFormContext();

  const handleToggle = () => {
    setIsOpen((prevOpen) => !prevOpen);
  };

  const campaignOptions = JOB_AD_CAMPAIGNS.sort((a, b) =>
    a.name.localeCompare(b.name),
  );

  const hideCustomerCompanyName = watch("hideCustomer");

  return (
    <>
      <FormSectionHeader
        isOpen={isOpen}
        toggleOpen={handleToggle}
        title={"EEZY.FI"}
        selectedJobAd={selectedJobAd}
        integrationType={integrationType}
        isEditing={isEditing}
        forcePublish
        integrationIsSelectedToBePublished={integrationIsSelectedToBePublished}
      />
      <Collapse in={isOpen} timeout="auto" unmountOnExit>
        <Grid
          container
          alignItems="center"
          columnSpacing={2}
          rowGap={1}
          maxWidth={"1000px"}
        >
          <FormGrid
            title={t("jobAds:discloseOrHideCustomer")}
            contentSize={2}
            required
          >
            <Controller
              name="hideCustomer"
              control={control}
              render={({ field: { value, onChange } }) => (
                <>
                  {isEditing ? (
                    <StyledSelect
                      value={value == null ? "false" : value.toString()}
                      onChange={(event) =>
                        onChange(event.target.value === "true")
                      }
                      displayEmpty
                      size="small"
                      MenuProps={{
                        disableScrollLock: true,
                      }}
                    >
                      {/* On UI label is "Disclose Customer?" but in Eezy API field is "hideCustomer", that's why "true -> no" and "false -> yes" */}
                      <MenuItem value="true">{t("no")}</MenuItem>
                      <MenuItem value="false">{t("yes")}</MenuItem>
                    </StyledSelect>
                  ) : (
                    <PreviewField text={value ? t("no") : t("yes")} />
                  )}
                </>
              )}
            />
          </FormGrid>

          <FormGrid
            title={t("jobAds:customerCompanyName")}
            contentSize={4}
            required={!hideCustomerCompanyName}
          >
            <Controller
              name="customerCompanyName"
              control={control}
              rules={{
                required:
                  integrationIsSelectedToBePublished &&
                  !hideCustomerCompanyName,
              }}
              render={({ field: { value, onChange } }) => (
                <>
                  {isEditing ? (
                    <Autocomplete
                      fullWidth
                      value={value || null}
                      options={[
                        ...customerOrganizations.map(
                          (co: CustomerOrganizationsResult) => co.name,
                        ),
                      ]}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          name="customerCompanyName"
                          error={!!errors.customerCompanyName}
                        />
                      )}
                      onChange={(_, newValue) => {
                        onChange(newValue);
                        // When value changes, also set new value to TMT integration's customerCompanyName
                        setValue("commissionCompanyName", newValue, {
                          shouldValidate: true,
                        });
                      }}
                    />
                  ) : (
                    <PreviewField text={value} />
                  )}
                </>
              )}
            />
          </FormGrid>

          {!hideCustomerCompanyName && (
            <>
              <FormGrid title={t("jobAds:customerDescription")} contentSize={8}>
                <Controller
                  name="customerDescription"
                  control={control}
                  rules={{
                    maxLength: 8000,
                  }}
                  render={({ field }) => (
                    <>
                      {isEditing ? (
                        <TextField {...field} multiline fullWidth rows={5} />
                      ) : (
                        <PreviewField text={field.value} />
                      )}
                    </>
                  )}
                />
              </FormGrid>
              <ImageUploadRow isEditing={isEditing} />
            </>
          )}

          <FormGrid
            title={t("jobAds:alternativeApplicationUrl")}
            contentSize={4}
          >
            {/* FIXME: fix unnecessary escape characters */}
            <Controller
              name="alternativeApplicationUrl"
              control={control}
              rules={{
                pattern:
                  /^https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_\+.~#?&\/=]*)$/,
              }}
              render={({ field }) => (
                <>
                  {isEditing ? (
                    <TextField
                      {...field}
                      size="small"
                      fullWidth
                      error={!!errors.alternativeApplicationUrl}
                    />
                  ) : (
                    <PreviewField text={field.value} />
                  )}
                </>
              )}
            />
          </FormGrid>

          <FormGrid title={t("jobAds:training")} contentSize={2}>
            <Controller
              name="training"
              control={control}
              render={({ field: { value, onChange } }) => (
                <>
                  {isEditing ? (
                    <StyledSelect
                      value={value === null ? "" : value.toString()}
                      onChange={(event) => {
                        const val = event.target.value;
                        if (val === "true") {
                          onChange(true);
                        } else if (val === "false") {
                          onChange(false);
                        } else {
                          onChange(null);
                        }
                      }}
                      displayEmpty
                      size="small"
                      MenuProps={{
                        disableScrollLock: true,
                      }}
                    >
                      <MenuItem value="">-</MenuItem>
                      <MenuItem value="true">{t("yes")}</MenuItem>
                      <MenuItem value="false">{t("no")}</MenuItem>
                    </StyledSelect>
                  ) : (
                    <PreviewField
                      text={value === null ? "-" : value ? t("yes") : t("no")}
                    />
                  )}
                </>
              )}
            />
          </FormGrid>

          <FormGrid title={t("jobAds:campaign")} contentSize={4}>
            <Controller
              control={control}
              name="campaigns"
              render={({ field: { value, onChange } }) => (
                <>
                  {isEditing ? (
                    <CustomAutocompleteMulti
                      name="campaigns"
                      defaultValue={value || []}
                      options={campaignOptions}
                      onChange={(_: any, newValue: AutocompleteType[]) =>
                        onChange(newValue)
                      }
                      isOptionEqualToValue={(option, value) =>
                        option.name === value.name
                      }
                    />
                  ) : (
                    <PreviewField
                      text={Object.values(value)
                        .map((v: any) => v.name)
                        .join(", ")}
                    />
                  )}
                </>
              )}
            />
          </FormGrid>

          <FormGrid
            title={isEditing ? "" : t("jobAds:temporaryWork")}
            contentSize={2}
          >
            <Controller
              control={control}
              name="temporaryWork"
              render={({ field: { value, onChange } }) => (
                <>
                  {isEditing ? (
                    <FormControlLabel
                      label={t("jobAds:temporaryWork")}
                      control={
                        <Checkbox
                          checked={value}
                          onChange={(event) => onChange(event.target.checked)}
                        />
                      }
                    />
                  ) : (
                    <PreviewField
                      text={value === null ? "-" : value ? t("yes") : t("no")}
                    />
                  )}
                </>
              )}
            ></Controller>
          </FormGrid>
        </Grid>
      </Collapse>
      <Divider
        sx={{
          margin: "24px 0 16px 0",
        }}
      />
    </>
  );
};

export default EezySection;
